import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { toast } from 'sonner';
import { Play, Pause, Square, Scissors, Download, Volume2, Mic, RefreshCw } from 'lucide-react';

/**
 * Neural News Browser-based Audio/Video Editor
 * Uses the Web Audio API for processing and Canvas for waveform visualization.
 * Supports: trim, fade-in/out, volume adjustment, export.
 */
export function AudioVideoEditor({ src, mediaType = 'audio', onExported }) {
  const audioRef         = useRef(null);
  const canvasRef        = useRef(null);
  const animFrameRef     = useRef(null);
  const audioCtxRef      = useRef(null);
  const analyserRef      = useRef(null);
  const sourceRef        = useRef(null);
  const audioBufferRef   = useRef(null);

  const [isPlaying,    setIsPlaying]    = useState(false);
  const [duration,     setDuration]     = useState(0);
  const [currentTime,  setCurrentTime]  = useState(0);
  const [volume,       setVolume]       = useState(1);
  const [trimStart,    setTrimStart]    = useState(0);
  const [trimEnd,      setTrimEnd]      = useState(100);   // % of duration
  const [fadeIn,       setFadeIn]       = useState(0);    // seconds
  const [fadeOut,      setFadeOut]      = useState(0);    // seconds
  const [waveformData, setWaveformData] = useState([]);
  const [processing,   setProcessing]  = useState(false);
  const [loadingWave,  setLoadingWave] = useState(false);

  // ── Load audio buffer for waveform + Web Audio processing ────────────
  useEffect(() => {
    if (!src) return;
    setLoadingWave(true);
    (async () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = ctx;
        const resp = await fetch(src);
        const arrayBuffer = await resp.arrayBuffer();
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer);
        audioBufferRef.current = audioBuffer;
        setDuration(audioBuffer.duration);
        setTrimEnd(100);

        // Build waveform data (downsampled)
        const raw = audioBuffer.getChannelData(0);
        const samples = 200;
        const blockSize = Math.floor(raw.length / samples);
        const wd = [];
        for (let i = 0; i < samples; i++) {
          let max = 0;
          for (let j = 0; j < blockSize; j++) {
            const abs = Math.abs(raw[i * blockSize + j]);
            if (abs > max) max = abs;
          }
          wd.push(max);
        }
        setWaveformData(wd);
      } catch (e) {
        toast.error('Could not decode audio file. Check the URL or format.');
      } finally {
        setLoadingWave(false);
      }
    })();
    return () => { if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {}); };
  }, [src]);

  // ── Draw waveform on canvas ──────────────────────────────────────────
  useEffect(() => {
    if (!canvasRef.current || !waveformData.length) return;
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const trimStartX = Math.round((trimStart / 100) * W);
    const trimEndX   = Math.round((trimEnd   / 100) * W);

    // Trimmed region
    ctx.fillStyle = 'rgba(184,155,94,0.08)';
    ctx.fillRect(trimStartX, 0, trimEndX - trimStartX, H);

    // Waveform bars
    waveformData.forEach((val, i) => {
      const x = Math.round((i / waveformData.length) * W);
      const barH = Math.max(2, val * H);
      const inTrim = x >= trimStartX && x <= trimEndX;
      ctx.fillStyle = inTrim ? '#B89B5E' : '#3A3A4A';
      ctx.fillRect(x, (H - barH) / 2, Math.max(2, W / waveformData.length - 1), barH);
    });

    // Trim handles
    ctx.fillStyle = '#B89B5E';
    ctx.fillRect(trimStartX - 2, 0, 4, H);
    ctx.fillRect(trimEndX   - 2, 0, 4, H);

    // Playhead
    if (duration > 0) {
      const playX = Math.round((currentTime / duration) * W);
      ctx.strokeStyle = '#F0EDE8';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(playX, 0);
      ctx.lineTo(playX, H);
      ctx.stroke();
    }
  }, [waveformData, trimStart, trimEnd, currentTime, duration]);

  // ── Handle canvas click to set playhead / trim points ────────────────
  const handleCanvasClick = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const pct  = ((e.clientX - rect.left) / rect.width) * 100;
    if (e.shiftKey) setTrimEnd(Math.max(trimStart + 1, pct));
    else if (e.ctrlKey || e.metaKey) setTrimStart(Math.min(trimEnd - 1, pct));
    else if (duration > 0 && audioRef.current) {
      audioRef.current.currentTime = (pct / 100) * duration;
    }
  }, [duration, trimStart, trimEnd]);

  // ── Playback ─────────────────────────────────────────────────────────
  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      const startSec = (trimStart / 100) * duration;
      if (audioRef.current.currentTime < startSec || audioRef.current.currentTime >= (trimEnd / 100) * duration) {
        audioRef.current.currentTime = startSec;
      }
      audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
    }
  };

  const stop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = (trimStart / 100) * duration;
    setIsPlaying(false);
  };

  // ── Export trimmed audio using Web Audio API ──────────────────────────
  const exportTrimmed = async () => {
    if (!audioBufferRef.current) { toast.error('No audio loaded'); return; }
    setProcessing(true);
    try {
      const buf = audioBufferRef.current;
      const sampleRate = buf.sampleRate;
      const startSample = Math.floor((trimStart / 100) * buf.length);
      const endSample   = Math.ceil( (trimEnd   / 100) * buf.length);
      const length = endSample - startSample;

      const offCtx = new OfflineAudioContext(buf.numberOfChannels, length, sampleRate);
      const src2   = offCtx.createBufferSource();

      // Create trimmed buffer
      const trimBuf = offCtx.createBuffer(buf.numberOfChannels, length, sampleRate);
      for (let ch = 0; ch < buf.numberOfChannels; ch++) {
        trimBuf.copyToChannel(buf.getChannelData(ch).slice(startSample, endSample), ch);
      }
      src2.buffer = trimBuf;

      // Volume + fades
      const gainNode = offCtx.createGain();
      gainNode.gain.setValueAtTime(volume, 0);
      if (fadeIn > 0) {
        gainNode.gain.setValueAtTime(0, 0);
        gainNode.gain.linearRampToValueAtTime(volume, fadeIn);
      }
      if (fadeOut > 0) {
        const fadeOutStart = length / sampleRate - fadeOut;
        gainNode.gain.setValueAtTime(volume, Math.max(0, fadeOutStart));
        gainNode.gain.linearRampToValueAtTime(0, length / sampleRate);
      }
      src2.connect(gainNode);
      gainNode.connect(offCtx.destination);
      src2.start();
      const rendered = await offCtx.startRendering();

      // Encode to WAV
      const wavBlob = audioBufferToWav(rendered);
      const url = URL.createObjectURL(wavBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `neuralnews-edit-${Date.now()}.wav`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${((trimEnd - trimStart) / 100 * duration).toFixed(1)}s clip`);
      onExported?.({ blob: wavBlob, duration: length / sampleRate });
    } catch (e) {
      toast.error('Export failed: ' + e.message);
    } finally {
      setProcessing(false);
    }
  };

  // ── WAV encoder ───────────────────────────────────────────────────────
  function audioBufferToWav(buffer) {
    const numChannels = buffer.numberOfChannels;
    const sampleRate  = buffer.sampleRate;
    const length      = buffer.length;
    const wavBuf      = new ArrayBuffer(44 + length * numChannels * 2);
    const view        = new DataView(wavBuf);
    const writeStr    = (offset, s) => { for (let i = 0; i < s.length; i++) view.setUint8(offset + i, s.charCodeAt(i)); };
    writeStr(0, 'RIFF');
    view.setUint32(4,  36 + length * numChannels * 2, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20,  1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * 2, true);
    view.setUint16(32, numChannels * 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, 'data');
    view.setUint32(40, length * numChannels * 2, true);
    let offset = 44;
    for (let i = 0; i < length; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const s = Math.max(-1, Math.min(1, buffer.getChannelData(ch)[i]));
        view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
        offset += 2;
      }
    }
    return new Blob([wavBuf], { type: 'audio/wav' });
  }

  const trimStartSec = ((trimStart / 100) * duration).toFixed(1);
  const trimEndSec   = ((trimEnd   / 100) * duration).toFixed(1);
  const clipDuration = (trimEndSec - trimStartSec).toFixed(1);

  return (
    <div className="bg-[#121316] rounded-2xl p-5 space-y-4 text-white">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Mic className="w-4 h-4 text-[#B89B5E]" /> Audio/Video Editor
        </h3>
        <span className="text-xs text-white/50">Web Audio API</span>
      </div>

      {/* Hidden media element */}
      {src && (
        mediaType === 'video'
          ? <video ref={audioRef} src={src} onTimeUpdate={e => setCurrentTime(e.target.currentTime)} onLoadedMetadata={e => setDuration(e.target.duration)} onEnded={() => setIsPlaying(false)} className="w-full rounded-xl max-h-48" controls={false} />
          : <audio ref={audioRef} src={src} onTimeUpdate={e => setCurrentTime(e.target.currentTime)} onLoadedMetadata={e => { if (!audioBufferRef.current) setDuration(e.target.duration); }} onEnded={() => setIsPlaying(false)} />
      )}

      {/* Waveform */}
      <div className="relative">
        {loadingWave ? (
          <div className="h-24 bg-white/5 rounded-xl flex items-center justify-center text-white/40 text-xs animate-pulse">Loading waveform…</div>
        ) : (
          <canvas
            ref={canvasRef}
            width={800}
            height={96}
            onClick={handleCanvasClick}
            className="w-full h-24 rounded-xl bg-white/5 cursor-crosshair"
            title="Click: move playhead | Ctrl+Click: set trim start | Shift+Click: set trim end"
          />
        )}
        <div className="absolute top-1 right-2 text-[9px] text-white/30">Ctrl+Click: trim start · Shift+Click: trim end</div>
      </div>

      {/* Trim info */}
      <div className="grid grid-cols-3 gap-3 text-center">
        <div className="bg-white/5 rounded-lg p-2">
          <p className="text-[10px] text-white/50">Trim Start</p>
          <p className="text-sm font-mono text-[#B89B5E]">{trimStartSec}s</p>
        </div>
        <div className="bg-[#B89B5E]/20 rounded-lg p-2">
          <p className="text-[10px] text-white/50">Clip Length</p>
          <p className="text-sm font-mono text-[#B89B5E]">{clipDuration}s</p>
        </div>
        <div className="bg-white/5 rounded-lg p-2">
          <p className="text-[10px] text-white/50">Trim End</p>
          <p className="text-sm font-mono text-[#B89B5E]">{trimEndSec}s</p>
        </div>
      </div>

      {/* Trim sliders */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-white/50 w-16">Trim Start</span>
          <input type="range" min={0} max={trimEnd - 1} value={trimStart} onChange={e => setTrimStart(parseFloat(e.target.value))} className="flex-1 accent-[#B89B5E]" />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-white/50 w-16">Trim End</span>
          <input type="range" min={trimStart + 1} max={100} value={trimEnd} onChange={e => setTrimEnd(parseFloat(e.target.value))} className="flex-1 accent-[#B89B5E]" />
        </div>
        <div className="flex items-center gap-3">
          <Volume2 className="w-3.5 h-3.5 text-white/50 flex-shrink-0" />
          <input type="range" min={0} max={2} step={0.05} value={volume} onChange={e => { setVolume(parseFloat(e.target.value)); if (audioRef.current) audioRef.current.volume = Math.min(1, parseFloat(e.target.value)); }} className="flex-1 accent-[#B89B5E]" />
          <span className="text-[10px] text-white/50 w-10">{Math.round(volume * 100)}%</span>
        </div>
      </div>

      {/* Fade controls */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] text-white/50 block mb-1">Fade In (seconds)</label>
          <input type="number" min={0} max={10} step={0.5} value={fadeIn} onChange={e => setFadeIn(parseFloat(e.target.value) || 0)} className="w-full bg-white/10 border border-white/10 rounded-lg px-2 py-1 text-xs text-white" />
        </div>
        <div>
          <label className="text-[10px] text-white/50 block mb-1">Fade Out (seconds)</label>
          <input type="number" min={0} max={10} step={0.5} value={fadeOut} onChange={e => setFadeOut(parseFloat(e.target.value) || 0)} className="w-full bg-white/10 border border-white/10 rounded-lg px-2 py-1 text-xs text-white" />
        </div>
      </div>

      {/* Controls */}
      <div className="flex gap-2">
        <Button size="sm" onClick={togglePlay} className="bg-[#B89B5E] text-white hover:bg-[#A08A50] flex-1">
          {isPlaying ? <Pause className="w-4 h-4 mr-1" /> : <Play className="w-4 h-4 mr-1" />}
          {isPlaying ? 'Pause' : 'Play Clip'}
        </Button>
        <Button size="sm" variant="outline" onClick={stop} className="border-white/20 text-white hover:bg-white/10">
          <Square className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="outline" onClick={() => { setTrimStart(0); setTrimEnd(100); setFadeIn(0); setFadeOut(0); setVolume(1); }} className="border-white/20 text-white hover:bg-white/10" title="Reset">
          <RefreshCw className="w-4 h-4" />
        </Button>
        <Button size="sm" onClick={exportTrimmed} disabled={processing || !waveformData.length} className="bg-white text-[#121316] hover:bg-white/90">
          {processing ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
          {processing ? 'Exporting…' : 'Export WAV'}
        </Button>
      </div>

      <p className="text-[10px] text-white/30 text-center">
        Exported audio is browser-processed. For server-side transcoding use <code className="text-white/50">POST /api/media/transcode</code>
      </p>
    </div>
  );
}
